import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
  SelectionProviderChildProps
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import {
  DatasourceFieldsByIdQuery,
  DatasourceFieldsByIdQuery_publishedDatasources_fieldsConnection_nodes,
  DatasourceFieldsByIdQuery_publishedDatasources_fieldsConnection_nodes_ColumnField,
  DatasourceFieldsByIdQueryVariables,
} from '../../../gen-src/queries/DatasourceFieldsByIdQuery';
import { FieldSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatasourceFieldsByIdQueryGql } from '../../queries/DatasourceFieldsByIdQueryGql';
import { DatasourceFieldsWithInheritanceByIdQueryGql } from '../../queries/DatasourceFieldsWithInheritanceByIdQueryGql';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';
import { DatasourceFieldsDisplayGrid, DatasourceFieldsDisplayGridRowData } from './DatasourceFieldsDisplayGrid';

export type QueryVariables = DatasourceFieldsByIdQueryVariables;
export type QueryResults = DatasourceFieldsByIdQuery;
export type QueryResultListNode = DatasourceFieldsByIdQuery_publishedDatasources_fieldsConnection_nodes;
export type GridRowData = DatasourceFieldsDisplayGridRowData;

export type DatasourceFieldsQueryGridProps =
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly rowCount: number | null;
    readonly hackyUpdateNumber: number;
  };

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.publishedDatasources && resultData.publishedDatasources[0] &&
    resultData.publishedDatasources[0].fieldsConnection;
}

export function mapResultNode(field: QueryResultListNode, resultData: QueryResults): GridRowData {
  return {
    ...field,
    dataType: (field as DatasourceFieldsByIdQuery_publishedDatasources_fieldsConnection_nodes_ColumnField).dataType || null,
    datasourceId: resultData.publishedDatasources[0].vizportalUrlId,
    fieldIcon: iconForField(field),
    sheetCount: field.downstreamSheetsConnection ? field.downstreamSheetsConnection.totalCount : null,
  };
}

export function DatasourceFieldsQueryGrid(props: DatasourceFieldsQueryGridProps): JSX.Element {
  const queryGqlClass =
    React.useContext(FeatureFlagsContext).fieldDescriptionInheritanceLineageEnabled
      ? DatasourceFieldsWithInheritanceByIdQueryGql
      : DatasourceFieldsByIdQueryGql;

  return (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={queryGqlClass}
      partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: props.orderBy as FieldSortOrder }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={mapResultNode}
      hackyUpdateNumber={props.hackyUpdateNumber}
    >
      {({ rowStore }) => (
        <DatasourceFieldsDisplayGrid
          rowStore={rowStore}
          selectedKeys={props.selectedKeys}
          updateSelectionForKey={props.updateSelectionForKey}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      )}
    </PaginatedQueryWrapper>
  );
}
